// api/brand-record.ts
//
// The brand record (#131, backend app/services/brand_record): one append-only
// store per client. Every signal writes an entry (a read's beliefs and
// competitors, the confirmed truth set, the voice profile, monitoring
// snapshots, publications and what they moved, market findings, events), each
// with its source, its date and a confidence. Nothing is overwritten: a new
// value supersedes the old one with a pointer to it. Admin-only.

import { apiFetch } from "./api-fetch";

export type RecordEntryType =
  | "belief" | "fact" | "voice" | "competitor" | "market"
  | "snapshot" | "outcome" | "publication" | "event";
export type RecordConfidence = "measured" | "confirmed" | "proposed";

export interface RecordEntry {
  id: number;
  entry_type: RecordEntryType;
  entry_key: string;
  value: Record<string, any>;
  retracted?: boolean;
  source_kind: string;
  source_ref: string;
  source_label: string;
  observed_at: string;
  confidence: RecordConfidence;
  run_id: number | null;
  segment: number | null;
  rebaseline_id?: number | null;
  supersedes_id: number | null;
  recorded_by?: string;
  recorded_at: string;
  previous?: RecordEntry | null;
}

export interface RecordCustomer {
  customer_id: number;
  brand_name: string;
  entries: number;
  last_recorded_at: string | null;
}

export interface BrandRecordView {
  customer_id: number;
  as_of: string | null;
  generated_at: string;
  record: Record<RecordEntryType, RecordEntry[]>;
  summary: { by_type: Partial<Record<RecordEntryType, number>>; last_recorded_at: string | null };
  pending_reads: { run_id: number; label: string }[];
  types: { id: RecordEntryType; label: string }[];
  source_kinds: string[];
}

export interface RecordChanges {
  since: string;
  count: number;
  by_type: Partial<Record<RecordEntryType, RecordEntry[]>>;
}

export interface MarketFindingInput {
  title: string;
  finding?: string;
  category?: string;
  confidence?: RecordConfidence;
  observed_at?: string;
  figures?: { label: string; value: string | number; unit?: string }[];
  source_document: { name: string; url?: string; page?: string };
}

export interface PublicationInput {
  title: string;
  published_at: string;
  url?: string;
  destination?: string;
  content?: string;
  targets?: { belief_ids?: string[]; question_ids?: string[] };
  action_id?: number;
  wind_tunnel_run_id?: number;
  wind_tunnel_draft_id?: string;
  note?: string;
  // #133: a publication record's own fields.
  approved_by?: string;
  question_types?: string[];
  draft_id?: number;
  draft_version?: number;
  action_item_id?: number;
}

const BASE = "/brand-record/customers";

function query(params: Record<string, string | number | undefined | null>): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== "") q.set(k, String(v));
  }
  const s = q.toString();
  return s ? `?${s}` : "";
}

export const brandRecordApi = {
  customers: () => apiFetch<{ customers: RecordCustomer[] }>(BASE),
  record: (customerId: number, asOf?: string) =>
    apiFetch<BrandRecordView>(`${BASE}/${customerId}${query({ as_of: asOf })}`),
  entries: (customerId: number, filters: { type?: string; source?: string; key?: string; since?: string; limit?: number }) =>
    apiFetch<{ entries: RecordEntry[]; count: number; limit: number }>(`${BASE}/${customerId}/entries${query(filters)}`),
  changes: (customerId: number, since: string) =>
    apiFetch<RecordChanges>(`${BASE}/${customerId}/changes${query({ since })}`),
  sync: (customerId: number) =>
    apiFetch<{ written: Record<string, number | string> }>(`${BASE}/${customerId}/sync`, { method: "POST" }),
  saveFindings: (customerId: number, findings: MarketFindingInput[]) =>
    apiFetch<{ written: number }>(`${BASE}/${customerId}/market`, {
      method: "POST",
      body: JSON.stringify({ findings }),
    }),
  recordPublication: (customerId: number, body: PublicationInput) =>
    apiFetch<{ written: number }>(`${BASE}/${customerId}/publications`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
};
